/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import Downshift from 'downshift';
import { Link, ToggletipLabel, Toggletip, ToggletipButton, ToggletipContent, TextInput } from '@carbon/react';
import Tag from '../../../Basic/Tag/Tag.js';
import { useTranslation } from '../../../../Utilities/i18n/i18n.js';
import { Information } from '@carbon/react/icons';

var usePrevious = function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value; // assign the value of ref to the argument
  }, [value]); // this code will run when the value of 'value' changes
  return ref.current; // in the end, return the current ref value.
};
var TagArea = function TagArea(_ref) {
  var autoFocus = _ref.autoFocus,
    defaultIsOpen = _ref.defaultIsOpen,
    disabled = _ref.disabled,
    hideLabel = _ref.hideLabel,
    showOptionalLabel = _ref.showOptionalLabel,
    id = _ref.id,
    inline = _ref.inline,
    isEditable = _ref.isEditable,
    light = _ref.light,
    labelColon = _ref.labelColon,
    onChange = _ref.onChange,
    onMessage = _ref.onMessage,
    options = _ref.options,
    tags = _ref.tags,
    toolTipDirection = _ref.toolTipDirection,
    toolTipLearnMoreLink = _ref.toolTipLearnMoreLink,
    keyValueFormat = _ref.keyValueFormat,
    tagPrefix = _ref.tagPrefix,
    clearInputValue = _ref.clearInputValue,
    allowAttach = _ref.allowAttach,
    allowDetach = _ref.allowDetach,
    allowCreate = _ref.allowCreate,
    tagAreaStrings = _ref.tagAreaStrings;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    inputValue = _useState2[0],
    setInputValue = _useState2[1];
  var _useState3 = useState(_toConsumableArray(tags)),
    _useState4 = _slicedToArray(_useState3, 2),
    attached = _useState4[0],
    setAttached = _useState4[1];
  var attachedOnOpen = _toConsumableArray(tags);
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    detached = _useState6[0],
    setDetached = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    invalid = _useState8[0],
    setInvalid = _useState8[1];
  var _useState9 = useState(''),
    _useState10 = _slicedToArray(_useState9, 2),
    invalidText = _useState10[0],
    setInvalidText = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    isFocused = _useState12[0],
    setIsFocused = _useState12[1];
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    isDirty = _useState14[0],
    setIsDirty = _useState14[1];
  var _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    refocusOnInput = _useState16[0],
    setRefocusOnInput = _useState16[1];
  var tagAreaContent = useRef();
  var menuWrap = document.querySelector("#tag-area-menu-wrap-".concat(id));
  var clearInputValueFunc = useCallback(function () {
    setAttached([]);
    setDetached([]);
    setInputValue('');
    setIsDirty(false);
    onChange({
      toAttach: [],
      toDetach: []
    });
  }, [onChange]);
  useEffect(function () {
    if (!isDirty) {
      setAttached(tags);
    }
  }, [isDirty, tags]);
  var prevClearInputValue = usePrevious(clearInputValue);
  useEffect(function () {
    if (!prevClearInputValue && clearInputValue) {
      clearInputValueFunc();
    }
  }, [clearInputValue, clearInputValueFunc, prevClearInputValue]);
  var prevAttached = usePrevious(attached);
  useEffect(function () {
    var tagsAdded = attached.length > (prevAttached === null || prevAttached === void 0 ? void 0 : prevAttached.length);
    var localTagAreaContent = tagAreaContent.current;
    if (tagsAdded && localTagAreaContent) {
      localTagAreaContent.scrollTop = localTagAreaContent.scrollHeight;
    }
  }, [attached.length, prevAttached === null || prevAttached === void 0 ? void 0 : prevAttached.length]);

  /**
   * Validates whether the characters and character count are valid for a tag or not.
   * @param {string} value A value to check against our validation conditions.
   */
  var validateTag = function validateTag(value) {
    var length = value.length;
    // Any alphanumeric value including one or more periods, dashes, or underscores.
    var characterValidation = /^[a-zA-Z0-9 .:_-]*$/;
    var keyValueValidation = /^[a-zA-Z0-9 .:_-]*:[a-zA-Z0-9 .:_-]*$/;
    var colonWithSpace = value.indexOf(': ') > -1 || value.indexOf(' :') > -1;
    var firstColonPosition = value.indexOf(':');
    var lastColonPosition = value.lastIndexOf(':');
    // The tag is greater than 128 characters in length.
    if (length > 128) {
      return t('A tag cannot include more than 128 characters.');
    }

    // The tag includes invalid characters.
    if (!value.match(characterValidation)) {
      return t('A tag can include only alphanumeric characters, spaces, underscores, dashes, periods and one colon.');
    }
    if (firstColonPosition === 0) {
      return t('A tag cannot start with a colon.');
    }
    if (length > 0 && lastColonPosition === length - 1) {
      return t('A tag cannot end with a colon.');
    }
    if (colonWithSpace) {
      return t('A tag should not have spaces around a colon.');
    }
    if (!allowAttach && keyValueFormat && !value.match(keyValueValidation)) {
      return t('The tag name must be in a key:value format');
    }
    return undefined;
  };
  var itemToString = function itemToString(item) {
    return item ? item.name : '';
  };

  /*
   * obj = {type:'error', message:'ooops, something happened'}
   */
  var messageHandler = function messageHandler(obj) {
    var toSend = obj.message ? obj : undefined;
    onMessage(toSend);
    setInvalid(!!obj.message);
    setInvalidText(obj.message ? obj.message : '');
  };
  var findInList = function findInList(newInputValue, tagList) {
    return tagList.find(function (tag) {
      return itemToString(tag).toLowerCase() === newInputValue.toLowerCase();
    });
  };

  /* Returns false if no duplicate is found; otherwise, returns true */
  var existsInList = function existsInList(newInputValue, tagList) {
    var retValue = tagList.find(function (tag) {
      return itemToString(tag).toLowerCase() === newInputValue.toLowerCase();
    });
    return retValue !== undefined;
  };
  var removeFromList = function removeFromList(newInputValue) {
    var list = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    return list.filter(function (item) {
      return itemToString(item).toLowerCase() !== newInputValue.toLowerCase();
    });
  };
  var addTagItem = function addTagItem(item) {
    if (item) {
      var nextAttached = [].concat(_toConsumableArray(attached), [item]);
      // it's being added, remove from detached list if it was detached previously
      var nextDetached = removeFromList(itemToString(item), detached);
      setAttached(nextAttached);
      setDetached(nextDetached);
      setInputValue('');
      setIsDirty(true);
      onChange({
        toAttach: nextAttached,
        toDetach: nextDetached
      });
    }
  };
  var focusOnElement = function focusOnElement(element) {
    return element.focus();
  };
  var handleTagAreaClick = function handleTagAreaClick(e) {
    var includesTagAreaContent = _toConsumableArray(e.target.classList).indexOf('cpc--tag-area__content') > -1;
    if (includesTagAreaContent && !disabled) {
      focusOnElement(e.target.querySelector("#".concat(id)));
    }
  };

  /**
   * Handles an input key down event. If a tab was pressed and shift was not held it sets state refocus on the input when the blur
   * event happens.
   * @param {event} event A synthetic React event.
   */
  var onInputKeyDown = function onInputKeyDown(event) {
    var tabPressed = event.key === 'tab' || (event.keyCode || event.which) === 9;
    var shiftWasNotPressed = !event.shiftKey;
    if (tabPressed && shiftWasNotPressed) {
      setRefocusOnInput(true);
    }
  };

  /**
   * Checks whether a tag should be added or not based on the event type and whether or not the user intended for the string to be added.
   * @param {Object} event A React event object
   */
  var onAddTagEvents = function onAddTagEvents(event, closeMenu) {
    // Keycode variables
    var keyCode = event.keyCode || event.which;
    var commaEntered = event.key === ',';
    var isBlurEvent = (event === null || event === void 0 ? void 0 : event.type) === 'blur';
    // Derived variables.
    var newInputValue = event.target.value.trim().toLowerCase();
    var addTagAttempted = keyCode === 13 || commaEntered || isBlurEvent;
    var listToCheck = attached;
    var keyValueValidation = /^[a-zA-Z0-9 .:_-]*:[a-zA-Z0-9 .:_-]*$/;
    // If the tag has more than 0 characters, has no errors, and an add event was attempted.
    if (newInputValue.length > 0 && !invalid && addTagAttempted) {
      // If the tag already exists in the provided list, fire off an error message instead.
      if (existsInList(newInputValue, listToCheck)) {
        messageHandler({
          type: 'error',
          message: t('This tag already exists. Choose another name for your tag.')
        });
        // close the menu here to show the error message.
        closeMenu();
      } else {
        var foundInList = findInList(newInputValue, options);
        if (!allowCreate && !foundInList) {
          messageHandler({
            type: 'error',
            message: t('No matches found. To create new tags, contact your tag administrator.')
          });
          // close the menu here to show the error message.
          closeMenu();
        } else if (keyValueFormat && !newInputValue.match(keyValueValidation)) {
          messageHandler({
            type: 'error',
            message: t('The tag name must be in a key:value format.')
          });
        } else {
          var tagToAdd = findInList(newInputValue, options) || {
            name: newInputValue,
            createTag: true
          };
          addTagItem(tagToAdd);
        }
      }
      // If tab was pressed refocus on input element. Escape execution to avoid loss of focus.
      if (refocusOnInput) {
        event.target.focus();
        setRefocusOnInput(false);
        return;
      }
    }
    // If it was a blur event change styles so, the input element looses focus.
    if (isBlurEvent) {
      setIsFocused(false);
    }
  };

  /**
   * Handles onChange events for the input element. Validates whether the tag's characters are valid
   * @param {string} inputValue A tag area input value to validate.
   * @param {Object} stateAndHelpers state and helpers passed through by Downshift.
   */
  var onInputValueChange = function onInputValueChange(newInputValue, stateAndHelpers) {
    // Only fire this event off if the event type is that of an autocomplete input change.
    if (stateAndHelpers.type === Downshift.stateChangeTypes.changeInput) {
      // Replace the trailing comma with an empty string.
      var inputWithoutTrailingComma = newInputValue.length > 1 ? newInputValue.replace(/,$/g, '') : newInputValue;
      setInputValue(inputWithoutTrailingComma);
      if (allowCreate && newInputValue !== '') {
        var errorMessage = validateTag(inputWithoutTrailingComma);
        messageHandler({
          type: 'error',
          message: errorMessage
        });
      } else {
        messageHandler({}); // clear previous error
      }
    }
  };
  var onRemove = function onRemove(tagObj) {
    var label = tagObj[1]; // tagObj[0] is the tag prefix or null if prefix is not specified
    // remove tag from the attached list
    var nextAttached = attached.filter(function (tag) {
      return tag.name !== label;
    });
    var nextDetached = _toConsumableArray(detached);
    // add tag to detach list only if it has a crn and it is an attached tag.
    var tagToDetach = tags.find(function (tag) {
      return tag.name === label;
    });
    if (tagToDetach) {
      nextDetached.push(tagToDetach);
    }
    setAttached(nextAttached);
    setDetached(nextDetached);
    setIsDirty(true);
    onChange({
      toAttach: nextAttached,
      toDetach: nextDetached
    });
  };

  // filters out options which are already in the attach list
  var filterMenuOptions = function filterMenuOptions(newInputValue) {
    return function (option) {
      if (!attached.some(function (item) {
        return item.name === option.name;
      })) {
        return newInputValue.length > 0 && itemToString(option).toLowerCase().indexOf(newInputValue.toLowerCase()) === 0;
      }
      return false;
    };
  };
  var renderTags = function renderTags() {
    return attached.map(function (tag) {
      var tagAlreadySaved = attachedOnOpen.find(function (savedTag) {
        return savedTag.name === tag.name;
      });
      var tagLabel = itemToString(tag);
      var tagId = tag.crn;
      return /*#__PURE__*/React.createElement(Tag, {
        tabIndex: "0",
        key: tagId || tagLabel,
        type: "functional",
        onRemove: onRemove,
        isRemovable: !disabled && (allowDetach || !tagAlreadySaved),
        maxCharacters: 25,
        title: tagLabel
      }, tagPrefix, tagLabel);
    });
  };
  var labelText = showOptionalLabel && ((tagAreaStrings === null || tagAreaStrings === void 0 ? void 0 : tagAreaStrings.labelTextOptional) || t('Tags: (optional)')) || (tagAreaStrings === null || tagAreaStrings === void 0 ? void 0 : tagAreaStrings.labelText) || t('Tags:');
  var TagAreaLabel = labelColon && labelText ? labelText : labelText.replace(/\s?:/, '');
  var TagAreaAriaLabel = "".concat(TagAreaLabel, " ").concat(attached.length > 0 ? attached.map(function (tag) {
    return tag.name;
  }).join() : (tagAreaStrings === null || tagAreaStrings === void 0 ? void 0 : tagAreaStrings.placeholder) || t('Examples: env:dev, version-1'));
  var TagAreaAriaDescription = "".concat(tagAreaStrings === null || tagAreaStrings === void 0 ? void 0 : tagAreaStrings.exampleOpeningLine, " ").concat(tagAreaStrings === null || tagAreaStrings === void 0 ? void 0 : tagAreaStrings.exampleTag);
  var showErrorMessage = inline && invalid;
  var tagAreaClass = classnames('cpc--resource-tag-area', {
    'cpc--resource-tag-area__readonly': !isEditable,
    'cpc--resource-tag-area__invalid': invalidText
  });
  var tagAreaContentClasses = classnames('cpc--resource-tag-area__content', 'cds--form-item', {
    'cpc--resource-tag-area__content-focused': isFocused,
    'cpc--text-input--light': light
  });
  var error = /*#__PURE__*/React.createElement("div", {
    className: classnames('cpc--resource-tag-area-invalid', {
      'cds--form-requirement': showErrorMessage
    }),
    id: "".concat(id, "-error-msg")
  }, showErrorMessage && invalidText);
  var learnMore = /*#__PURE__*/React.createElement(Link, {
    target: "_blank",
    href: toolTipLearnMoreLink
  }, t('Learn more'));
  return /*#__PURE__*/React.createElement("div", {
    className: tagAreaClass,
    onClick: handleTagAreaClick
  }, !hideLabel && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ToggletipLabel, null, TagAreaLabel), /*#__PURE__*/React.createElement(Toggletip, {
    align: toolTipDirection
  }, /*#__PURE__*/React.createElement(ToggletipButton, {
    label: t('Show tooltip')
  }, /*#__PURE__*/React.createElement(Information, null)), /*#__PURE__*/React.createElement(ToggletipContent, null, /*#__PURE__*/React.createElement("p", null, tagAreaStrings === null || tagAreaStrings === void 0 ? void 0 : tagAreaStrings.exampleOpeningLine, (tagAreaStrings === null || tagAreaStrings === void 0 ? void 0 : tagAreaStrings.exampleTag) && /*#__PURE__*/React.createElement("span", {
    className: "cpc--tag-tooltip-example"
  }, tagAreaStrings.exampleTag), !(tagAreaStrings !== null && tagAreaStrings !== void 0 && tagAreaStrings.tagVisibility) && /*#__PURE__*/React.createElement(React.Fragment, null, " ", learnMore)), (tagAreaStrings === null || tagAreaStrings === void 0 ? void 0 : tagAreaStrings.tagVisibility) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("p", null, tagAreaStrings === null || tagAreaStrings === void 0 ? void 0 : tagAreaStrings.tagVisibility, learnMore))))), /*#__PURE__*/React.createElement("div", {
    className: tagAreaContentClasses,
    ref: tagAreaContent
  }, isEditable ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "cpc--resource-tag-area__aria-description-hidden",
    id: "".concat(id, "__aria-description")
  }, TagAreaAriaDescription), /*#__PURE__*/React.createElement(Downshift, {
    defaultIsOpen: defaultIsOpen,
    onSelect: addTagItem,
    onInputValueChange: onInputValueChange,
    itemToString: itemToString,
    inputValue: inputValue
  }, function (_ref2) {
    var getInputProps = _ref2.getInputProps,
      getItemProps = _ref2.getItemProps,
      getRootProps = _ref2.getRootProps,
      isOpen = _ref2.isOpen,
      newInputValue = _ref2.inputValue,
      highlightedIndex = _ref2.highlightedIndex,
      closeMenu = _ref2.closeMenu;
    return /*#__PURE__*/React.createElement("div", _extends({
      className: "cpc--resource-tag-area__tag-input-wrap"
    }, getRootProps({
      role: 'listbox',
      'aria-haspopup': undefined,
      'aria-label': TagAreaAriaLabel,
      'aria-describedby': "".concat(id, "__aria-description"),
      'aria-labelledby': undefined,
      'aria-owns': undefined
    }, {
      suppressRefError: true
    })), /*#__PURE__*/React.createElement(TextInput, _extends({}, getInputProps({
      id: id,
      placeholder: attached.length > 0 ? '' : (tagAreaStrings === null || tagAreaStrings === void 0 ? void 0 : tagAreaStrings.placeholder) || t('Examples: env:dev, version-1'),
      onKeyUp: function onKeyUp(event) {
        return onAddTagEvents(event, closeMenu);
      },
      onKeyDown: onInputKeyDown,
      role: 'textbox',
      'aria-controls': undefined,
      'aria-expanded': undefined,
      'aria-activedescendant': undefined,
      'aria-labelledby': undefined,
      'aria-owns': undefined
    }), {
      labelText: TagAreaLabel,
      hideLabel: true,
      onFocus: function onFocus() {
        return setIsFocused(true);
      },
      onBlur: function onBlur(event) {
        return onAddTagEvents(event, closeMenu);
      },
      autoFocus: autoFocus,
      disabled: disabled
    })), isOpen && menuWrap && !showErrorMessage ? /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement("div", {
      className: "cpc--resource-tag-area__menu"
    }, options.filter(filterMenuOptions(newInputValue)).map(function (item, index) {
      var menuItemClasses = classnames('cpc--resource-tag-area__menu-item', {
        'cpc--resource-tag-area__menu-item--highlighted': highlightedIndex === index
      });
      return /*#__PURE__*/React.createElement("div", _extends({
        className: menuItemClasses
      }, getItemProps({
        item: item
      }), {
        key: item.name
      }), /*#__PURE__*/React.createElement(Tag, {
        tabIndex: "0",
        key: itemToString(item),
        type: "functional",
        maxCharacters: 25,
        title: itemToString(item)
      }, tagPrefix, item.name));
    })), menuWrap) : null);
  })) : /*#__PURE__*/React.createElement("div", {
    className: "cpc--resource-tag-area__tag-input-wrap"
  }, /*#__PURE__*/React.createElement(TextInput, {
    id: id,
    labelText: TagAreaLabel,
    hideLabel: true,
    disabled: true
  })), renderTags()), error, /*#__PURE__*/React.createElement("div", {
    className: "cpc--resource-tag-area__menu-wrap",
    id: "tag-area-menu-wrap-".concat(id)
  }));
};
TagArea.propTypes = {
  autoFocus: PropTypes.bool,
  defaultIsOpen: PropTypes.bool,
  disabled: PropTypes.bool,
  hideLabel: PropTypes.bool,
  showOptionalLabel: PropTypes.bool,
  id: PropTypes.string.isRequired,
  inline: PropTypes.bool,
  isEditable: PropTypes.bool,
  light: PropTypes.bool,
  labelColon: PropTypes.bool,
  onChange: PropTypes.func,
  onMessage: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  tags: PropTypes.arrayOf(PropTypes.shape({})),
  toolTipDirection: PropTypes.string,
  toolTipLearnMoreLink: PropTypes.string,
  keyValueFormat: PropTypes.bool,
  tagPrefix: PropTypes.string,
  clearInputValue: PropTypes.bool,
  allowAttach: PropTypes.bool,
  allowDetach: PropTypes.bool,
  allowCreate: PropTypes.bool,
  tagAreaStrings: PropTypes.shape({
    exampleTag: PropTypes.node,
    exampleOpeningLine: PropTypes.node,
    tagVisibility: PropTypes.node,
    placeholder: PropTypes.node,
    labelTextOptional: PropTypes.node,
    labelText: PropTypes.node
  })
};
TagArea.defaultProps = {
  autoFocus: false,
  defaultIsOpen: true,
  disabled: false,
  hideLabel: false,
  showOptionalLabel: false,
  inline: false,
  isEditable: true,
  light: true,
  labelColon: true,
  onChange: function onChange(toAttach, toDetach) {},
  // eslint-disable-line no-unused-vars
  onMessage: function onMessage(obj) {
    return obj;
  },
  options: [],
  tags: [],
  toolTipDirection: 'bottom',
  toolTipLearnMoreLink: '/docs/account?topic=account-tag',
  keyValueFormat: false,
  tagPrefix: '',
  clearInputValue: false,
  allowAttach: true,
  allowDetach: true,
  allowCreate: true,
  tagAreaStrings: undefined
};
var TagAreaComponent = TagArea;

export { TagAreaComponent as default };
